import { useCallback, useEffect, useRef } from 'react';
import { useAuthStore, useUser } from '@/stores/authStore';
import {
  initKeycloak,
  login as keycloakLogin,
  register as keycloakRegister,
  accountManagement as keycloakAccountManagement,
  refreshToken,
} from '@/lib/keycloak';

/**
 * Options for the useAuth hook
 */
export interface UseAuthOptions {
  /** Minimum token validity in seconds before refresh (default: 30) */
  minValidity?: number;
  /** Interval in ms to check token expiration (default: 30000) */
  checkInterval?: number;
  /** Auto-redirect to login if not authenticated (default: false) */
  autoLogin?: boolean;
}

/**
 * Return type for the useAuth hook
 */
export interface UseAuthReturn {
  /** Whether the auth system is initialized */
  isInitialized: boolean;
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** Current user info */
  user: ReturnType<typeof useUser>;
  /** Trigger login flow */
  login: () => void;
  /** Send the user to the Keycloak registration form (not the sign in form) */
  register: () => void;
  /** Trigger logout flow */
  logout: () => void;
  /** Redirect to the Keycloak-hosted Account Console (profile, password, sessions) */
  accountManagement: () => void;
  /** Manually refresh the token */
  refresh: () => Promise<boolean>;
  /** Token expiration timestamp (Unix epoch) */
  tokenExpiration: number | null;
  /** Whether the token is close to expiring (within 60 seconds) */
  isExpiringSoon: boolean;
}

/**
 * Hook for managing authentication state and operations.
 *
 * Integrates Keycloak adapter with the auth store to provide a unified
 * authentication interface for React components.
 *
 * Features:
 * - Automatic Keycloak initialization on mount
 * - Periodic token refresh to prevent session expiration
 * - Session expiration detection
 * - Optional auto-login redirect
 *
 * @example
 * // Basic usage
 * function MyComponent() {
 *   const { isAuthenticated, user, login, logout } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <button onClick={login}>Login</button>;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Welcome, {user?.name}</p>
 *       <button onClick={logout}>Logout</button>
 *     </div>
 *   );
 * }
 *
 * @example
 * // With auto-login for protected routes
 * function ProtectedPage() {
 *   const { isInitialized, isAuthenticated } = useAuth({ autoLogin: true });
 *
 *   if (!isInitialized) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   // Will auto-redirect to login if not authenticated
 *   return <ProtectedContent />;
 * }
 *
 * @example
 * // Checking token expiration
 * function SessionWarning() {
 *   const { isExpiringSoon, refresh } = useAuth();
 *
 *   if (isExpiringSoon) {
 *     return (
 *       <Alert>
 *         Your session is expiring soon.
 *         <button onClick={refresh}>Extend Session</button>
 *       </Alert>
 *     );
 *   }
 *
 *   return null;
 * }
 */
export function useAuth(options: UseAuthOptions = {}): UseAuthReturn {
  const { minValidity = 30, checkInterval = 30000, autoLogin = false } = options;

  const {
    isInitialized,
    isAuthenticated,
    tokenExpiration,
    setInitialized,
    updateFromKeycloak,
    logout,
  } = useAuthStore();

  const user = useUser();
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef<boolean>(true);

  // Check if token is expiring soon (within 60 seconds)
  const isExpiringSoon = tokenExpiration
    ? tokenExpiration - Math.floor(Date.now() / 1000) < 60
    : false;

  // Initialize Keycloak on mount
  useEffect(() => {
    mountedRef.current = true;

    async function init() {
      if (isInitialized) return;

      try {
        await initKeycloak();
        if (mountedRef.current) {
          updateFromKeycloak();
          setInitialized(true);
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        if (mountedRef.current) {
          // Still mark as initialized to prevent infinite retry loops
          setInitialized(true);
        }
      }
    }

    void init();

    return () => {
      mountedRef.current = false;
    };
  }, [isInitialized, setInitialized, updateFromKeycloak]);

  // Auto-login if required and not authenticated
  useEffect(() => {
    if (isInitialized && !isAuthenticated && autoLogin) {
      keycloakLogin();
    }
  }, [isInitialized, isAuthenticated, autoLogin]);

  // Token refresh logic - check and refresh periodically
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear any existing interval when not authenticated
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      return;
    }

    async function checkAndRefreshToken() {
      if (!mountedRef.current) return;

      try {
        const refreshed = await refreshToken(minValidity);
        if (refreshed) {
          updateFromKeycloak();
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
      }
    }

    // Initial check
    void checkAndRefreshToken();

    // Set up periodic interval
    refreshIntervalRef.current = setInterval(() => {
      void checkAndRefreshToken();
    }, checkInterval);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, [isAuthenticated, minValidity, checkInterval, updateFromKeycloak]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, []);

  const login = useCallback(() => {
    keycloakLogin();
  }, []);

  const register = useCallback(() => {
    keycloakRegister();
  }, []);

  const accountManagement = useCallback(() => {
    keycloakAccountManagement();
  }, []);

  const refresh = useCallback(async (): Promise<boolean> => {
    try {
      const success = await refreshToken(minValidity);
      if (success) {
        updateFromKeycloak();
      }
      return success;
    } catch (error) {
      console.error('Manual token refresh failed:', error);
      return false;
    }
  }, [minValidity, updateFromKeycloak]);

  return {
    isInitialized,
    isAuthenticated,
    user,
    login,
    register,
    logout,
    accountManagement,
    refresh,
    tokenExpiration,
    isExpiringSoon,
  };
}

export default useAuth;
